import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faPenToSquare,
  faArrowUpRightDots,
  faBellSlash,
  faBan,
  faFileLines,
  faBoxArchive,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { cn } from '@/lib/cn';
import type { AdoptionStatus } from '@/services/email-template-library.service';

/**
 * Presentation for the seven adoption states (Phase 12.5C).
 *
 * The wording matters more than usual here, because these labels are the only
 * thing telling a tenant what its customers will actually receive. Two rules the
 * copy is built around:
 *
 *  · DISABLED is a HARD opt-out (D-030) — the renderer throws rather than falling
 *    back to the platform master. Anything implying "the default will still go
 *    out" would be untrue, so the copy says emails are suppressed.
 *  · An unpublished draft is deliberately NOT an adoption state. A tenant with a
 *    draft is still on whatever it was on before, and saying otherwise would
 *    misreport live behaviour. Drafts are surfaced as a separate badge.
 */

interface AdoptionPresentation {
  label: string;
  icon: IconDefinition;
  /** Badge classes — theme tokens only, readable in light and dark. */
  className: string;
  /** One sentence describing what recipients receive in this state. */
  meaning: string;
}

export const ADOPTION_PRESENTATION: Record<AdoptionStatus, AdoptionPresentation> = {
  USING_PLATFORM_DEFAULT: {
    label: 'Platform default',
    icon: faCircleCheck,
    className: 'bg-muted text-muted-foreground ring-border',
    meaning:
      'This template uses the version maintained by your service provider. Updates arrive automatically.',
  },
  CUSTOMIZED: {
    label: 'Customized',
    icon: faPenToSquare,
    className: 'bg-primary/10 text-primary ring-primary/25',
    meaning:
      'Your workspace sends its own version of this template. Platform updates are offered, never applied automatically.',
  },
  PLATFORM_UPDATE_AVAILABLE: {
    label: 'Update available',
    icon: faArrowUpRightDots,
    className: 'bg-accent/20 text-foreground ring-accent/40',
    meaning:
      'Your service provider has published a newer version. Your workspace still sends your customized version until you adopt it.',
  },
  IGNORED: {
    label: 'Update ignored',
    icon: faBellSlash,
    className: 'bg-muted text-muted-foreground ring-border',
    meaning:
      'You dismissed the current update. Your customized version keeps sending, and you will be notified again on the next release.',
  },
  DISABLED: {
    label: 'Disabled',
    icon: faBan,
    className: 'bg-destructive/10 text-destructive ring-destructive/25',
    meaning:
      'This template is disabled for your workspace. Emails using this template are currently suppressed.',
  },
  DRAFT: {
    label: 'Not active',
    icon: faFileLines,
    className: 'bg-muted text-muted-foreground ring-border',
    meaning: 'This template is not active for your workspace, so nothing is sent.',
  },
  ARCHIVED: {
    label: 'Archived',
    icon: faBoxArchive,
    className: 'bg-muted text-muted-foreground ring-border',
    meaning: 'This template is archived and is no longer used.',
  },
};

export function AdoptionBadge({
  status,
  className,
}: {
  status: AdoptionStatus;
  className?: string;
}) {
  const p = ADOPTION_PRESENTATION[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1',
        p.className,
        className,
      )}
    >
      <FontAwesomeIcon icon={p.icon} className="h-3 w-3" aria-hidden />
      {p.label}
    </span>
  );
}

/** Separate from the adoption badge on purpose — a draft is not an adoption state. */
export function DraftBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-400',
        className,
      )}
    >
      <FontAwesomeIcon icon={faPenToSquare} className="h-3 w-3" aria-hidden />
      Unpublished draft
    </span>
  );
}

export const ADOPTION_FILTERS: { value: AdoptionStatus | ''; label: string }[] = [
  { value: '', label: 'All states' },
  { value: 'USING_PLATFORM_DEFAULT', label: 'Platform default' },
  { value: 'CUSTOMIZED', label: 'Customized' },
  { value: 'PLATFORM_UPDATE_AVAILABLE', label: 'Update available' },
  { value: 'IGNORED', label: 'Update ignored' },
  { value: 'DISABLED', label: 'Disabled' },
];

export function formatRelative(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}
